import React, { useState } from 'react';
import { CircularProgress, Dialog, DialogContent, Typography } from '@material-ui/core';
import { CHMessagingScope, OverlayDialogButtonAction, IOverlayDialog } from '@wk/elm-uui-context-handler';
import { FallbackProps } from 'react-error-boundary';
import css from './overlayDialog.module.scss';
import UUIButton from './uuiButton';
import IconMapper from './iconMapper';
import { hardNavigateToHomePage, logOutSession } from '../../utils/utilities';
import { useReduxDispatch } from '../../hooks/useReduxDispatch';
import { useHomePageUrl } from '../../hooks/useHomePageUrl';
import { clNotifyAutoUpdateService } from '../contextLayerService/contextLayerService';
import { messageBusDispatch } from '../contextLayerService/messageBusService';
import { useUUIHistory } from '../../hooks/useUUIHistory';
import { useReduxSelector } from '../../hooks/useReduxSelector';

type IOverlayDialogProps = IOverlayDialog & Partial<FallbackProps>;

export const OverlayDialog: React.FC<IOverlayDialogProps> = ({
    heading,
    icon,
    message,
    button,
    showSpinner,
    resetErrorBoundary,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const homePageUrl = useHomePageUrl();
    const history = useUUIHistory();
    const reduxDispatch = useReduxDispatch();
    const appResources = useReduxSelector((state) => state.appResources);

    const installUpdate = (): void => {
        clNotifyAutoUpdateService();
        reduxDispatch({ type: 'CloseOverlayDialog' });
    };

    const navigateToHome = (): void => {
        if (resetErrorBoundary) {
            resetErrorBoundary();
        }
        hardNavigateToHomePage(homePageUrl, history);
    };

    const logOut = (): void => {
        logOutSession(appResources.logoutUrl);
    };

    const reload = (): void => {
        messageBusDispatch({
            type: 'ReloadUUI',
            scope: CHMessagingScope.OtherInstances,
            message: '',
        });
        location.reload();
    };

    const actions = {
        [OverlayDialogButtonAction.Reload]: reload,
        [OverlayDialogButtonAction.InstallUpdate]: installUpdate,
        [OverlayDialogButtonAction.NavigateToHome]: navigateToHome,
        [OverlayDialogButtonAction.logOff]: logOut,
    };

    return (
        <Dialog
            fullScreen
            disableEnforceFocus
            className={css.dialogRoot}
            data-testid={'fullScreenDialog'}
            open={isOpen}
            onClose={() => setIsOpen(false)}
            PaperProps={{ 'aria-label': 'Full Screen Overlay Dialog' }}
            classes={{ paper: css.dialogBody }}
            hideBackdrop={true}>
            <DialogContent className={css.dialogContent}>
                <Typography variant="h5" className={css.heading}>
                    {heading}
                </Typography>
                <div className={css.iconWrapper}>
                    <IconMapper iconName={icon} />
                </div>
                {showSpinner && (
                    <div className={css.spinner}>
                        <CircularProgress aria-label="progressBar" size={30} thickness={5.0} color={'secondary'} />
                    </div>
                )}
                {message.map((msg, index) => (
                    <Typography key={index} variant="body1" className={css.messageBody} data-testid="overlay-message">
                        {msg}
                    </Typography>
                ))}
                {button && (
                    <div className={css.buttonWrapper}>
                        <UUIButton
                            data-testid={'customOverlayButton'}
                            variant="outlined"
                            color="primary"
                            onClick={actions[button.action]}>
                            {button.text}
                        </UUIButton>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
